import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { useState, useCallback } from "react";
import { WidgetEditUnsuppressProvider, ShareConversationDialogWidget } from "@brandclub/bc-widgets";
import { ResponsiveDialog } from "../../Components/Dialog/ResponsiveDialog.js";
const Spinner = () => /* @__PURE__ */ jsxs(
  "svg",
  {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    style: { animation: "share-dialog-spin 0.8s linear infinite" },
    children: [
      /* @__PURE__ */ jsx("circle", { cx: "8", cy: "8", r: "6.5", stroke: "currentColor", strokeWidth: "2", opacity: "0.3" }),
      /* @__PURE__ */ jsx(
        "path",
        {
          d: "M14.5 8a6.5 6.5 0 00-6.5-6.5",
          stroke: "currentColor",
          strokeWidth: "2",
          strokeLinecap: "round"
        }
      )
    ]
  }
);
const ShareConversationDialog = ({
  open,
  onClose,
  onCopyLink,
  brandLogoUrl
}) => {
  const [shareState, setShareState] = useState("idle");
  const handleCopyLink = useCallback(async () => {
    if (shareState !== "idle") return;
    setShareState("loading");
    try {
      await onCopyLink();
      setShareState("copied");
    } catch {
      setShareState("idle");
    }
  }, [shareState, onCopyLink]);
  const handleClose = useCallback(() => {
    setShareState("idle");
    onClose();
  }, [onClose]);
  const buttonText = shareState === "copied" ? "Link copied!" : "Copy link";
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("style", { children: `
        @keyframes share-dialog-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @media(max-width:899.95px){.share-dialog-content{border-bottom-left-radius:0!important;border-bottom-right-radius:0!important}}
      ` }),
    /* @__PURE__ */ jsx(
      ResponsiveDialog,
      {
        open,
        onClose: handleClose,
        disableScrollLock: true,
        sx: (theme) => ({
          zIndex: 1e4,
          "& div.MuiDialog-paper": {
            margin: "0 20px",
            width: "100%",
            maxWidth: 440,
            overflow: "visible",
            backgroundColor: "transparent",
            boxShadow: "none",
            paddingTop: 0,
            [theme.breakpoints.down("md")]: {
              maxWidth: "100%",
              width: "100%",
              margin: 0,
              padding: 0,
              paddingTop: "12px",
              boxSizing: "border-box"
            }
          }
        }),
        children: /* @__PURE__ */ jsx(WidgetEditUnsuppressProvider, { children: /* @__PURE__ */ jsx(
          ShareConversationDialogWidget,
          {
            widgetId: "concierge-share-conversation-dialog",
            displayName: "Share Conversation Dialog",
            children: (styles, resolveColor, resolvedLogoUrl) => {
              const logoSrc = resolvedLogoUrl || brandLogoUrl;
              return /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "share-dialog-content",
                  style: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    alignItems: "stretch",
                    padding: `${styles.dialogPadding}px`,
                    backgroundColor: resolveColor(styles.dialogBgColor),
                    borderRadius: `${styles.dialogBorderRadius}px`,
                    boxShadow: "0 5px 10px 0 rgba(181, 176, 176, 0.16)"
                  },
                  children: [
                    /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "16px"
                        },
                        children: [
                          logoSrc && /* @__PURE__ */ jsx(
                            "div",
                            {
                              style: {
                                width: `${styles.logoContainerSize}px`,
                                height: `${styles.logoContainerSize}px`,
                                borderRadius: "100px",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: resolveColor(styles.logoContainerBgColor),
                                flexShrink: 0
                              },
                              children: /* @__PURE__ */ jsx(
                                "img",
                                {
                                  src: logoSrc,
                                  alt: "",
                                  style: {
                                    width: `${styles.logoContainerSize * 0.75}px`,
                                    height: `${styles.logoContainerSize * 0.75}px`,
                                    objectFit: "contain"
                                  }
                                }
                              )
                            }
                          ),
                          /* @__PURE__ */ jsx(
                            "p",
                            {
                              style: {
                                fontSize: `${styles.titleFontSize}px`,
                                fontWeight: 600,
                                lineHeight: "1.1",
                                textAlign: "center",
                                color: resolveColor(styles.titleColor),
                                margin: 0
                              },
                              children: "Share this conversation"
                            }
                          ),
                          /* @__PURE__ */ jsx(
                            "p",
                            {
                              style: {
                                fontSize: `${styles.descriptionFontSize}px`,
                                fontWeight: 500,
                                lineHeight: "1.5",
                                textAlign: "center",
                                color: resolveColor(styles.descriptionColor),
                                margin: 0
                              },
                              children: "Want to share this conversation? Copy the link below — we'll only include what's been said so far."
                            }
                          )
                        ]
                      }
                    ),
                    /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: "12px"
                        },
                        children: [
                          /* @__PURE__ */ jsxs(
                            "button",
                            {
                              type: "button",
                              onClick: handleCopyLink,
                              disabled: shareState === "loading",
                              style: {
                                width: "100%",
                                backgroundColor: resolveColor(styles.primaryButtonBgColor),
                                color: resolveColor(styles.primaryButtonTextColor),
                                border: "none",
                                borderRadius: `${styles.primaryButtonBorderRadius}px`,
                                padding: "13px 16px",
                                fontSize: "14px",
                                fontWeight: 600,
                                lineHeight: "20px",
                                cursor: shareState === "loading" ? "wait" : "pointer",
                                opacity: shareState === "loading" ? 0.7 : 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                transition: "opacity 0.15s ease"
                              },
                              children: [
                                shareState === "loading" && /* @__PURE__ */ jsx(Spinner, {}),
                                buttonText
                              ]
                            }
                          ),
                          /* @__PURE__ */ jsx(
                            "button",
                            {
                              type: "button",
                              onClick: handleClose,
                              style: {
                                background: "none",
                                border: "none",
                                padding: 0,
                                fontSize: "14px",
                                fontWeight: 600,
                                lineHeight: "20px",
                                color: resolveColor(styles.cancelTextColor),
                                cursor: "pointer"
                              },
                              children: "Cancel"
                            }
                          )
                        ]
                      }
                    )
                  ]
                }
              );
            }
          }
        ) })
      }
    )
  ] });
};
export {
  ShareConversationDialog
};
//# sourceMappingURL=ShareConversationDialog.js.map
